import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    Button,
    Modal,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import { CoverageCardType } from '../../../../graphql/generated/graphql';
import { useCoverageAssistanceMutation } from '../../../../hooks/use-coverage-assistance-mutation';
import { useDrugKey } from '../../../../hooks/use-drug-key';

export type DeclineSelfPayAssistanceModalProps = {
    isOpen: boolean;
    close: () => void;
};

export const DeclineSelfPayAssistanceModal: FC<
    DeclineSelfPayAssistanceModalProps
> = ({ isOpen, close }) => {
    const { t } = useTranslation('lilly');
    const { allOffers, drxId, medication } = useFillPrescription();
    const { setPathway } = useFillPathway();
    const medConfig = useMedicationConfigOverride();

    const drugKey = useDrugKey(medication);
    const listPrice = allOffers.find(
        (o) => o.headlinePriceType === 'Cash'
    )?.patientCost;

    const declineCallback = useCallback(async () => {
        close();
        setPathway({ pathway: 'fallback' });
    }, [close, setPathway]);

    const { decline, loading } = useCoverageAssistanceMutation({
        cardType: CoverageCardType.CashCard,
        declineCallback,
        drxId,
    });

    const drugName = drugKey
        ? medConfig.drugNames[drugKey]
        : t('fill.selfPay.decline.modal.unknownDrug');

    return (
        <Modal
            opened={isOpen}
            onClose={close}
            title={t('fill.selfPay.decline.modal.title')}
        >
            <Stack gap="lg">
                <Subtitle2>
                    {t('fill.selfPay.decline.modal.subtitle')}
                </Subtitle2>
                <Body1>
                    {t('fill.selfPay.decline.modal.line1', {
                        drugName,
                        price: listPrice,
                    })}
                </Body1>
                <Body1>
                    {t('fill.selfPay.decline.modal.line2', {
                        price: listPrice,
                    })}
                </Body1>
                <Stack gap="xs">
                    <Button
                        variant="filled"
                        disabled={loading}
                        onClick={close}
                        size="lg"
                    >
                        {t('fill.selfPay.decline.modal.backBtn')}
                    </Button>
                    <Button
                        variant="subtle"
                        loading={loading}
                        onClick={decline}
                        size="lg"
                    >
                        {t('fill.selfPay.decline.modal.continueBtn')}
                    </Button>
                </Stack>
            </Stack>
        </Modal>
    );
};
